import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import React, { useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Ionicons } from "@expo/vector-icons";
import { useDevice } from "../../_layout";
import Note from "../../Firestore/Note";
import Tooltip from "../../Components/Tooltip";
import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";
import { canAccessSummaryNotes } from "../../invitations/invitedClientUiGuards";

const NotesCard = observer(({ client, showNotesCard }) => {
  const [selectedDay, setSelectedDay] = useState("Mon");
  const { t } = useTranslation();
  const dailyNoteRef = React.useRef(null);
  const [isDailyNoteExpanded, setIsDailyNoteExpanded] = useState(false);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [isExpandButtonHovered, setIsExpandButtonHovered] = useState(false);
  const { isMobile } = useDevice();

  // Get the week days based on summaryStartDate
  const weekStart = client.summaryStartDate;
  const weekDays = [];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  if (weekStart) {
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      weekDays.push({
        dayName: dayNames[date.getDay()],
        date: date,
      });
    }
  }

  // Build a map of daily notes for this week
  const dailyNotesMap = {};
  if (client.notesArray && weekStart) {
    weekDays.forEach(({ dayName, date }) => {
      const noteForDay = client.notesArray.find((note) => {
        if (!note.date) return false;
        const noteDate = new Date(note.date);
        return (
          noteDate.getFullYear() === date.getFullYear() &&
          noteDate.getMonth() === date.getMonth() &&
          noteDate.getDate() === date.getDate()
        );
      });
      if (noteForDay) {
        dailyNotesMap[dayName] = noteForDay;
      }
    });
  }

  const hasPrivateNote = client.notes && client.notes.trim().length > 0;
  const hasDailyNotes = Object.keys(dailyNotesMap).length > 0;

  const handlePrivateNoteChange = (text) => {
    client.setProperty("notes", text);
    client.updateClientTransactionProfile(client.id);
    client.startOrResetBatchTimer();
  };

  const handleDailyNoteChange = (dayName, text) => {
    const dayInfo = weekDays.find((d) => d.dayName === dayName);
    if (!dayInfo) return;

    const existingNote = dailyNotesMap[dayName];

    if (existingNote) {
      // Update existing note
      existingNote.setProperty("text", text);
      client.updateClientTransactionNotes(existingNote.id);
      client.startOrResetBatchTimer();
    } else {
      // Create new note if text is not empty
      if (text && text.trim().length > 0) {
        const newNote = new Note(undefined, dayInfo.date, text, client);
        client.notesArray.push(newNote);
        client.updateClientTransactionNotes(newNote.id);
        client.startOrResetBatchTimer();
        // Update the map so it reflects immediately
        dailyNotesMap[dayName] = newNote;
      }
    }
  };

  const handleDailyNoteBlur = (dayName) => {
    const existingNote = dailyNotesMap[dayName];
    if (
      existingNote &&
      (!existingNote.text || existingNote.text.trim().length === 0)
    ) {
      client.updateClientDeletionNotes(existingNote.id);
      //Delete the note from client.notesArray
      client.setProperty(
        "notesArray",
        client.notesArray.filter((note) => note.id !== existingNote.id)
      );
      client.startOrResetBatchTimer();
    }
  };

  // Auto-focus the daily note textarea when it appears and is empty
  React.useEffect(() => {
    if (
      isDailyNoteExpanded &&
      !dailyNotesMap[selectedDay]?.text &&
      dailyNoteRef.current
    ) {
      dailyNoteRef.current.focus();
    }
  }, [isDailyNoteExpanded, selectedDay]);

  // Early returns after all hooks
  if (!canAccessSummaryNotes(client)) return null;
  if (!client.summaryStartDate) return null;

  // Don't show card if no data and not explicitly requested
  if (!showNotesCard && !hasPrivateNote && !hasDailyNotes) {
    return null;
  }

  return (
    <View style={GlobalStyles.Card}>
      <View style={GlobalStyles.CardHeader}>
        <View style={GlobalStyles.CardHeaderMain}>
          <View style={[GlobalStyles.CardIcon, GlobalStyles.SummaryCardIcon]}>
            <Ionicons
              name="document-text-outline"
              size={16}
              color={Colors.grayColor}
            />
          </View>
          <Text style={GlobalStyles.CardTitle}>{t("notesTitle-string")}</Text>
        </View>
      </View>

      <View style={styles.notesContainer}>
        {/* Private Coach Note */}
        <View style={styles.noteSection}>
          <View style={styles.noteHeaderRow}>
            <Ionicons
              name="lock-closed-outline"
              size={12}
              color={Colors.grayColor}
            />
            <Text style={styles.noteSectionLabel}>
              {t("privateNote-string")}
            </Text>
            <Tooltip
              content="privateNoteInfo-string"
              placement="top"
              maxWidth={300}
            >
              <View style={styles.tooltipIconContainer}>
                <Ionicons
                  name="information-circle-outline"
                  size={14}
                  color={Colors.grayColor}
                />
              </View>
            </Tooltip>
          </View>
          <View style={{ borderRadius: 8, overflow: "hidden" }}>
            <TextareaAutosize
              style={{
                ...styles.cleanInput,
                resize: "none",
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                border: "none",
                outline: "none",
                fontSize: isMobile ? 16 : 13,
              }}
              placeholder={t("onlyVisibleToYou-string")}
              value={client.notes || ""}
              onChange={(e) => handlePrivateNoteChange(e.target.value)}
              minRows={2}
              maxRows={10}
              maxLength={1000}
              autoCorrect="off"
              spellCheck="false"
            />
          </View>
        </View>

        {/* Athlete Daily Notes */}
        <View style={styles.noteSection}>
          <View
            style={[
              styles.noteHeaderRow,
              { justifyContent: "space-between" },
              !isDailyNoteExpanded &&
                Object.keys(dailyNotesMap).length === 0 && {
                  marginBottom: 0,
                },
            ]}
          >
            <View style={styles.noteHeaderLabel}>
              <Ionicons
                name="calendar-outline"
                size={12}
                color={Colors.grayColor}
              />
              <Text style={styles.noteSectionLabel}>
                {t("dailyNotes-string")}
              </Text>
              <Tooltip
                content="dailyNotesInfo-string"
                placement="top"
                maxWidth={300}
              >
                <View style={styles.tooltipIconContainer}>
                  <Ionicons
                    name="information-circle-outline"
                    size={14}
                    color={Colors.grayColor}
                  />
                </View>
              </Tooltip>
            </View>
            <TouchableOpacity
              onPress={() => setIsDailyNoteExpanded(!isDailyNoteExpanded)}
              onMouseEnter={() => !isMobile && setIsExpandButtonHovered(true)}
              onMouseLeave={() => setIsExpandButtonHovered(false)}
              style={styles.iconButton}
            >
              <Ionicons
                name={isDailyNoteExpanded ? "chevron-up" : "add"}
                size={16}
                color={
                  isExpandButtonHovered
                    ? Colors.darkgrayColor
                    : Colors.grayColor
                }
              />
            </TouchableOpacity>
          </View>

          {isDailyNoteExpanded ? (
            <View style={styles.dailyNoteEditor}>
              <View style={styles.daysTabs}>
                {weekDays.map(({ dayName }) => (
                  <TouchableOpacity
                    key={dayName}
                    style={[
                      styles.dayTab,
                      selectedDay === dayName && styles.dayTabSelected,
                    ]}
                    onPress={() => setSelectedDay(dayName)}
                    onMouseEnter={() => !isMobile && setHoveredDay(dayName)}
                    onMouseLeave={() => setHoveredDay(null)}
                  >
                    <Text
                      style={[
                        styles.dayTabText,
                        selectedDay === dayName && styles.dayTabTextSelected,
                        hoveredDay === dayName &&
                          selectedDay !== dayName && {
                            color: Colors.darkgrayColor,
                          },
                      ]}
                    >
                      {dayName}
                    </Text>
                    {dailyNotesMap[dayName] ? (
                      <View style={styles.dayDot} />
                    ) : null}
                  </TouchableOpacity>
                ))}
              </View>

              <TextareaAutosize
                ref={dailyNoteRef}
                style={{
                  ...styles.cleanInput,
                  resize: "none",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  border: "none",
                  outline: "none",
                  fontSize: isMobile ? 16 : 13,
                }}
                placeholder={t("noteForDay-string", { day: selectedDay })}
                value={dailyNotesMap[selectedDay]?.text || ""}
                onChange={(e) =>
                  handleDailyNoteChange(selectedDay, e.target.value)
                }
                onBlur={() => handleDailyNoteBlur(selectedDay)}
                minRows={2}
                maxRows={10}
                maxLength={600}
                autoCorrect="off"
                spellCheck="false"
              />
            </View>
          ) : Object.keys(dailyNotesMap).length > 0 ? (
            <View style={styles.dailyNotesList}>
              {Object.entries(dailyNotesMap).map(([dayName, note]) => (
                <TouchableOpacity
                  key={dayName}
                  style={[
                    styles.dailyNoteSummary,
                    // hoveredDailySummary === dayName && {
                    //   backgroundColor: Colors.lightGrayColor,
                    //   borderRadius: 8,
                    // },
                  ]}
                  onPress={() => {
                    setSelectedDay(dayName);
                    setIsDailyNoteExpanded(true);
                  }}
                >
                  <View style={styles.dailyNoteBadge}>
                    <Text style={styles.dailyNoteBadgeText}>{dayName}</Text>
                  </View>
                  <Text style={styles.dailyNoteSummaryText} numberOfLines={10}>
                    {note.text}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  notesContainer: {
    gap: 16,
  },
  noteSection: {
    gap: 8,
  },
  noteHeaderRow: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 4,
    gap: 6,
  },
  noteHeaderLabel: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  noteSectionLabel: {
    fontSize: 11,
    fontWeight: "600",
    color: Colors.grayColor,
    letterSpacing: 0.5,
  },
  cleanInput: {
    backgroundColor: Colors.lightGrayColor,
    borderRadius: 8,
    padding: 10,
    fontSize: 13,
    color: Colors.blackColor,
    minHeight: 40,
    textAlignVertical: "top",
  },
  dailyNoteEditor: {
    gap: 8,
  },
  dailyNotesList: {
    gap: 6,
  },
  dailyNoteSummary: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 4,
  },
  dailyNoteBadge: {
    backgroundColor: Colors.lightGrayColor,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  dailyNoteBadgeText: {
    fontSize: 10,
    fontWeight: "600",
    color: Colors.grayColor,
  },
  dailyNoteSummaryText: {
    fontSize: 12,
    color: Colors.blackColor,
    flex: 1,
  },
  daysTabs: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
    flexWrap: "wrap",
    gap: 0,
  },
  dayTab: {
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderRadius: 6,
    gap: 4,
    minWidth: 38,
  },
  dayTabSelected: {
    backgroundColor: Colors.lightGrayColor,
  },
  dayTabText: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.grayColor,
  },
  dayTabTextSelected: {
    color: Colors.darkgrayColor,
    fontWeight: "600",
  },
  dayDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: Colors.noteColor,
  },
  tooltipIconContainer: {
    marginLeft: 2,
    justifyContent: "center",
    alignItems: "center",
  },
  iconButton: {
    padding: 4,
  },
});

export default NotesCard;
